import { Monitor } from '@sortlist-frontend/mlm/server';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import {
  assertNonEmptyString,
  assertNonEmptyStringOrNull,
  HttpBadRequest,
  HttpException,
} from '@sortlist-frontend/utils';
import { dehydrate, DehydratedState, QueryClient } from '@tanstack/react-query';
/* eslint-disable sonarjs/cognitive-complexity */
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { Fragment, useRef } from 'react';

import { PageHead } from '_components/base/head';
import { PublicGoogleTagManager } from '_components/common';
import { Public404Page } from '_components/error/public-404-page';
import { LayoutPagesRouter } from '_components/layout/LayoutPagesRouter';
import { cacheConfig } from '_config/cache.config';
import { extractDomainRelevantInfoFromHeaders } from '_config/domain.config';
import { REDIRECTIONS } from '_config/redirections';
import { AppProviders } from '_core/app-providers';
import { PublicAppContextProvider, usePublicAppContext } from '_core/context/public-app-context';
import { getUserConsentCookie } from '_core/cookies';
import { isSerializedError, SerializedError, serializeError } from '_core/error/error-serializer';
import {
  cacheKey,
  getPublicApiRequestLink,
  publicApiRepo,
  PublicApiRepo,
  useGetLinks,
  useGetPage,
} from '_core/repos/public-api.repo';
import { baseSsrPrefetchQueries } from '_core/utils/public-api';
import { getLocalizedUrl } from '_core/utils/public-links/getLocalizedUrl';
import { getServerSideTranslations } from '_core/utils/ssr';
import { customConfig } from '_features/custom/custom.config';
import { ExplorePage } from '_features/explore/explore.page';
import { DirectoryEmbedPage } from '_features/longtail/embed';
import { filteredLinks } from '_features/longtail/utils';
import StoriesPage from '_features/stories/stories.page';
import StoryPage from '_features/story/story.page';
import { AnchorType, Page, PageContent } from '_types/public-api';

type Prop = {
  locale?: string;
  canonical: string;
  query?: string;
  origin?: string;
  userConsent: string | null;
  error: SerializedError | null;
  page?: string;
  url?: string;
  publicApiNavBarUrl?: string;
  embed?: boolean;
  abValue?: string;
  dehydratedState?: DehydratedState;
};

type CustomRouteInnerProps = {
  embed: boolean;
};

const prefetchAllQuery = async (frontendType: string, pageInfo: Page, url: string, queryClient: QueryClient) => {
  return await Promise.allSettled([
    queryClient.prefetchQuery({ queryKey: [cacheKey.page(url)], queryFn: () => pageInfo }),
  ]);
};

const CustomRouteInner = ({ embed }: CustomRouteInnerProps) => {
  const scrollableCtn = useRef<HTMLDivElement>(null);
  const { data: ssrData } = useGetPage(PublicApiRepo.publicApiUrl);
  const { data: links } = useGetLinks(PublicApiRepo.publicApiUrl);
  const { canonical, query, domainInfo, locale } = usePublicAppContext();
  const frontendType = ssrData?.data?.attributes?.frontend_type;

  const alternatesLinks = links ? filteredLinks(links, 'hreflang') : [];
  const isExplorePage = frontendType === 'explore';
  const isStoriesPage = frontendType === 'stories';
  const isStoryPage = frontendType === 'story';

  return (
    <Fragment>
      <PageHead
        showAlternates={true}
        alternateLinks={alternatesLinks}
        canonical={canonical}
        query={query}
        origin={domainInfo?.getOriginUrl() as string}
        locale={locale as string}
      />

      <LayoutPagesRouter
        scrollableCtn={scrollableCtn}
        showFooter={!embed}
        nav={!embed}
        showSubnav={false}
        showRankUpdated>
        {isExplorePage && <ExplorePage scrollableCtn={scrollableCtn} />}
        {embed && <DirectoryEmbedPage />}
        {isStoriesPage && <StoriesPage />}
        {isStoryPage && <StoryPage />}
      </LayoutPagesRouter>
    </Fragment>
  );
};

export const Route = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const {
    error,
    locale = 'en',
    canonical,
    query,
    origin = '',
    url = '',
    publicApiNavBarUrl = '',
    embed = false,
    abValue,
  } = props;
  const { data: ssrData } = useGetPage(url);
  const { t } = useTranslation(customConfig.i18nNamespaces);

  if (error && isSerializedError(error)) {
    const { status } = error;
    if (status === 404) return <Public404Page message={t('common:pageErrors.occured')} locale={locale} />;
  }

  PublicApiRepo.publicApiUrl = url;
  PublicApiRepo.publicApiNavBarUrl = publicApiNavBarUrl;

  if (ssrData) {
    const { briefing_options, frontend_type } = { ...ssrData?.data?.attributes };
    const { expertise } = { ...briefing_options };
    const expertiseId = Number(expertise?.id) || undefined;

    return (
      <PublicAppContextProvider
        canonical={canonical}
        query={query}
        origin={origin}
        locale={locale}
        abValue={abValue}
        briefingOptions={{
          page: frontend_type,
          address: briefing_options?.location?.address,
          placeId: briefing_options?.location?.place_id,
          expertise: expertiseId,
          expertiseObject: expertise && { expertiseId, ...expertise },
          skills: briefing_options?.skills,
          skillObjects: ssrData.data.attributes.briefing_options?.skill_objects,
        }}>
        <PublicGoogleTagManager />

        <CustomRouteInner embed={embed} />
      </PublicAppContextProvider>
    );
  }

  return <Public404Page message={t('common:pageErrors.occured')} locale={locale} />;
};

export default function CustomRoute(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <AppProviders dehydratedState={props.dehydratedState}>
      <Route {...props} />
    </AppProviders>
  );
}

const cacheOptions = cacheConfig.pages['longtails'] || { httpCacheControl: '' };

export const getServerSideProps: GetServerSideProps<Prop> = async (context) => {
  const { resolvedUrl, query, req } = context;
  const queryParams = Object.entries(query)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  const { sort = 'position', page = '1' } = query;
  const { i18nNamespaces } = customConfig;

  // example of retrieving the user consent that was set by the user in the front-end
  const userConsent = getUserConsentCookie(req);

  let error: SerializedError | null = null;
  let domainRelevantInfo;
  try {
    domainRelevantInfo = extractDomainRelevantInfoFromHeaders(req);
  } catch (e) {
    if (e instanceof HttpException) {
      context.res.statusCode = e.status;
      error = serializeError(e);
    } else {
      Monitor.captureException(e instanceof Error ? e : new Error('Unexpected SSR error in explore page'), {
        extra: { url: context.resolvedUrl },
        tags: { context: 'getServerSideProps' },
      });
      throw e;
    }

    return {
      props: {
        error,
        userConsent,
        canonical: '',
        ...(await getServerSideTranslations('en', i18nNamespaces)),
      },
    };
  }

  const { canonical, origin, locale } = domainRelevantInfo;

  assertNonEmptyStringOrNull(sort, new HttpBadRequest({ message: '[sort] empty or multiple values found' }));
  assertNonEmptyStringOrNull(page, new HttpBadRequest({ message: '[page] empty or multiple values found' }));

  const embed = resolvedUrl.includes('/embed');
  const url = getPublicApiRequestLink(origin, locale, context.resolvedUrl.replace('/hidden/', '/'));

  const queryClient = new QueryClient();

  let canonicalLink = '';
  let publicApiNavBarUrl = '';
  let hasCanonicalPage = false;
  let pageInfo;
  try {
    pageInfo = await publicApiRepo.getPage({ page: `${url}?include=canonical_page,redirection_page` });
    const { frontend_type: frontendType } = pageInfo.data.attributes;

    const relationshipsData = pageInfo.data.relationships;
    hasCanonicalPage = relationshipsData?.canonical_page?.data != null;

    const findRelationCanonicalPage = pageInfo?.included?.find(
      (page) => page.id === relationshipsData.canonical_page.data?.id,
    ) as PageContent | undefined;

    canonicalLink = getLocalizedUrl({
      ...findRelationCanonicalPage?.attributes,
      link: findRelationCanonicalPage?.attributes.path,
    } as AnchorType);

    await prefetchAllQuery(frontendType, pageInfo, url, queryClient);

    const response = await baseSsrPrefetchQueries(origin, locale, context.resolvedUrl, queryClient, true);
    publicApiNavBarUrl = response.publicApiNavBarUrl;
  } catch (e) {
    // Limitation: Typescript exceptions are not typed, so we rely on what is supposed to happen
    if ((e as any).status !== 404) {
      throw e;
    }

    // @TODO: use a more elegant way when upgrade to NextJS 10 https://nextjs.org/blog/next-10#redirect-and-notfound-support-for-getstaticprops--getserversideprops
    context.res.statusCode = 404;
    error = serializeError(e as Error);
  }

  if (!context.res.headersSent) {
    context.res.setHeader('Cache-Control', cacheOptions.httpCacheControl);
  }

  return {
    props: {
      locale,
      canonical: hasCanonicalPage ? canonicalLink : canonical,
      query: queryParams,
      origin,
      dehydratedState: dehydrate(queryClient),
      userConsent,
      error,
      page,
      url,
      publicApiNavBarUrl,
      embed,
      ...(await getServerSideTranslations(locale, i18nNamespaces)),
    },
  };
};
